import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ScheduledJobApiService } from '../../../shared/services/scheduled-job-api.service';
import {
  ScheduledJobConfigResponse,
  ScheduledJobConfigRequest,
  ScheduledJobFrequencyType,
} from '../../../shared/models/scheduled-job.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

/** UI-level frequency types that map to API types */
type UIFrequencyType = 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';

const JOB_KEY = 'currency.update-rates';

@Component({
  selector: 'app-currency-job-configs',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent],
  templateUrl: './currency-job-configs.component.html',
})
export class CurrencyJobConfigsComponent implements OnInit {
  jobs: ScheduledJobConfigResponse[] = [];
  loading = false;
  error = '';

  showModal = false;
  editingJob: ScheduledJobConfigResponse | null = null;
  saving = false;
  runningJobId: number | null = null;

  // Form fields
  formDescription = '';
  formEnabled = true;
  formFrequencyType: UIFrequencyType = 'DAILY';
  formStartTime = '08:00';
  formTimezone = this.getDefaultTimezone();
  formIntervalValue = 1;
  formDaysOfWeek: string[] = [];
  formDayOfMonth = 1;
  formCronExpression = '0 0 8 * * *';
  formMaxRetries = 0;
  formRetryBackoffSeconds = 5;

  readonly timezones = [
    'UTC',
    'America/Mexico_City',
    'America/Bogota',
    'America/Lima',
    'America/Santiago',
    'America/Buenos_Aires',
    'America/Sao_Paulo',
    'America/New_York',
    'Europe/Madrid',
  ];

  readonly hourlyOptions = [1, 2, 3, 4, 6, 8, 12];
  readonly daysOfWeekOptions = [
    { value: 'MON', label: 'Lun' },
    { value: 'TUE', label: 'Mar' },
    { value: 'WED', label: 'Mié' },
    { value: 'THU', label: 'Jue' },
    { value: 'FRI', label: 'Vie' },
    { value: 'SAT', label: 'Sáb' },
    { value: 'SUN', label: 'Dom' },
  ];

  private getDefaultTimezone(): string {
    try {
      const user = JSON.parse(localStorage.getItem('current_user') ?? 'null');
      if (user?.timezone) return user.timezone;
      const business = JSON.parse(
        localStorage.getItem('current_business') ?? 'null',
      );
      if (business?.defaultTimezone) return business.defaultTimezone;
    } catch {
      /* ignore */
    }
    return 'UTC';
  }

  constructor(
    private scheduledJobApi: ScheduledJobApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadJobs();
  }

  loadJobs(): void {
    this.loading = true;
    this.error = '';
    this.scheduledJobApi.list(JOB_KEY).subscribe({
      next: (data) => {
        this.jobs = data ?? [];
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar los jobs.';
        this.loading = false;
      },
    });
  }

  openCreate(): void {
    this.editingJob = null;
    this.formDescription = 'Actualización automática de tasas de cambio';
    this.formEnabled = true;
    this.formFrequencyType = 'DAILY';
    this.formStartTime = '08:00';
    this.formTimezone = this.getDefaultTimezone();
    this.formIntervalValue = 1;
    this.formDaysOfWeek = [];
    this.formDayOfMonth = 1;
    this.formCronExpression = '0 0 8 * * *';
    this.formMaxRetries = 0;
    this.formRetryBackoffSeconds = 5;
    this.showModal = true;
  }

  openEdit(j: ScheduledJobConfigResponse): void {
    this.editingJob = j;
    this.formDescription = j.description ?? '';
    this.formEnabled = j.enabled;
    this.formTimezone = j.timezone ?? 'UTC';
    this.formMaxRetries = j.maxRetries ?? 0;
    this.formRetryBackoffSeconds = j.retryBackoffSeconds ?? 5;

    // Reverse-map API frequency to UI type
    if (j.frequencyType === 'PERIODIC') {
      this.formFrequencyType = 'HOURLY';
      this.formIntervalValue = Math.round((j.intervalSeconds ?? 3600) / 3600);
    } else if (j.frequencyType === 'CRON' && j.cronExpression) {
      const parsed = this.parseCronToUI(j.cronExpression);
      this.formFrequencyType = parsed.uiType;
      this.formStartTime = parsed.startTime;
      this.formDaysOfWeek = parsed.daysOfWeek;
      this.formDayOfMonth = parsed.dayOfMonth;
      this.formCronExpression = j.cronExpression;
    } else {
      this.formFrequencyType = 'CUSTOM';
      this.formCronExpression = j.cronExpression ?? '0 0 8 * * *';
    }

    this.showModal = true;
  }

  /** Attempt to parse a cron expression back to a UI-friendly type */
  private parseCronToUI(cron: string): {
    uiType: UIFrequencyType;
    startTime: string;
    daysOfWeek: string[];
    dayOfMonth: number;
  } {
    const parts = cron.split(/\s+/);
    // Default fallback
    const result = {
      uiType: 'CUSTOM' as UIFrequencyType,
      startTime: '08:00',
      daysOfWeek: [] as string[],
      dayOfMonth: 1,
    };
    if (parts.length !== 6) return result;

    const [_sec, min, hour, dom, _mon, dow] = parts;

    // Daily: 0 MM HH * * *
    if (
      dom === '*' &&
      dow === '*' &&
      !hour.includes('/') &&
      !hour.includes(',')
    ) {
      result.uiType = 'DAILY';
      result.startTime = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
      return result;
    }

    // Weekly: 0 MM HH * * MON,WED...
    if (dom === '*' && dow !== '*' && !hour.includes('/')) {
      result.uiType = 'WEEKLY';
      result.startTime = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
      result.daysOfWeek = dow.split(',');
      return result;
    }

    // Monthly: 0 MM HH D * *
    if (
      dom !== '*' &&
      dow === '*' &&
      !hour.includes('/') &&
      !dom.includes('/')
    ) {
      result.uiType = 'MONTHLY';
      result.startTime = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
      result.dayOfMonth = parseInt(dom, 10) || 1;
      return result;
    }

    return result;
  }

  toggleDay(day: string): void {
    const idx = this.formDaysOfWeek.indexOf(day);
    if (idx >= 0) {
      this.formDaysOfWeek = this.formDaysOfWeek.filter((d) => d !== day);
    } else {
      this.formDaysOfWeek = [...this.formDaysOfWeek, day];
    }
  }

  isDaySelected(day: string): boolean {
    return this.formDaysOfWeek.includes(day);
  }

  /** Map UI frequency to API ScheduledJobConfigRequest */
  buildRequest(): ScheduledJobConfigRequest {
    const req: ScheduledJobConfigRequest = {
      jobKey: JOB_KEY,
      description: this.formDescription || undefined,
      enabled: this.formEnabled,
      frequencyType: this.mapFrequencyType(),
      timezone: this.formTimezone,
      maxRetries: this.formMaxRetries,
      retryBackoffSeconds: this.formRetryBackoffSeconds,
    };

    if (this.formFrequencyType === 'HOURLY') {
      req.frequencyType = 'PERIODIC';
      req.intervalSeconds = this.formIntervalValue * 3600;
    } else if (this.formFrequencyType === 'DAILY') {
      req.cronExpression = this.buildCronFromTime(this.formStartTime);
    } else if (this.formFrequencyType === 'WEEKLY') {
      const days =
        this.formDaysOfWeek.length > 0 ? this.formDaysOfWeek.join(',') : '*';
      req.cronExpression = this.buildCronFromTime(
        this.formStartTime,
        '*',
        days,
      );
    } else if (this.formFrequencyType === 'MONTHLY') {
      req.cronExpression = this.buildCronFromTime(
        this.formStartTime,
        String(this.formDayOfMonth),
      );
    } else {
      req.cronExpression = this.formCronExpression;
    }

    return req;
  }

  private mapFrequencyType(): ScheduledJobFrequencyType {
    return this.formFrequencyType === 'HOURLY' ? 'PERIODIC' : 'CRON';
  }

  private buildCronFromTime(time: string, dom = '*', dow = '*'): string {
    const [h, m] = time.split(':');
    return `0 ${parseInt(m, 10)} ${parseInt(h, 10)} ${dom} * ${dow}`;
  }

  save(): void {
    this.saving = true;
    const req = this.buildRequest();
    const obs = this.editingJob
      ? this.scheduledJobApi.update(this.editingJob.id, req)
      : this.scheduledJobApi.create(req);
    obs.subscribe({
      next: () => {
        this.saving = false;
        this.showModal = false;
        this.loadJobs();
      },
      error: () => {
        this.saving = false;
      },
    });
  }

  async deleteJob(j: ScheduledJobConfigResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar job',
      message: '¿Eliminar este job de actualización de tasas?',
    });
    if (!ok) return;
    this.scheduledJobApi.delete(j.id).subscribe({
      next: () => {
        this.loadJobs();
        this.toast.deleted('Job');
      },
      error: () => {
        this.toast.error({
          title: 'Error',
          message: 'Error al eliminar el job.',
        });
      },
    });
  }

  toggleJob(j: ScheduledJobConfigResponse): void {
    this.scheduledJobApi.setEnabled(j.id, !j.enabled).subscribe({
      next: () => this.loadJobs(),
      error: () =>
        this.confirmDialog.alert({
          title: 'Error',
          message: 'Error al cambiar el estado.',
          variant: 'danger',
        }),
    });
  }

  runNow(j: ScheduledJobConfigResponse): void {
    this.runningJobId = j.id;
    this.scheduledJobApi.runNow(j.id).subscribe({
      next: (exec) => {
        this.runningJobId = null;
        if (exec.status === 'SUCCESS') {
          this.confirmDialog.alert({
            title: 'Éxito',
            message: 'Ejecución exitosa.',
            variant: 'success',
          });
        } else {
          this.confirmDialog.alert({
            title: 'Error',
            message: `Ejecución fallida`,
            variant: 'danger',
          });
        }
      },
      error: () => {
        this.runningJobId = null;
        this.confirmDialog.alert({
          title: 'Error',
          message: 'Error al ejecutar el job.',
          variant: 'danger',
        });
      },
    });
  }

  getFrequencyLabel(j: ScheduledJobConfigResponse): string {
    if (j.frequencyType === 'PERIODIC') {
      const hours = Math.round((j.intervalSeconds ?? 3600) / 3600);
      return `Cada ${hours}h`;
    }
    if (j.frequencyType === 'CRON' && j.cronExpression) {
      const parsed = this.parseCronToUI(j.cronExpression);
      switch (parsed.uiType) {
        case 'DAILY':
          return `Diario · ${parsed.startTime}`;
        case 'WEEKLY': {
          const dayLabels = parsed.daysOfWeek
            .map(
              (d) =>
                this.daysOfWeekOptions.find((o) => o.value === d)?.label ?? d,
            )
            .join(', ');
          return dayLabels
            ? `Semanal · ${dayLabels} · ${parsed.startTime}`
            : `Semanal · ${parsed.startTime}`;
        }
        case 'MONTHLY':
          return `Mensual · día ${parsed.dayOfMonth} · ${parsed.startTime}`;
        default:
          return `Personalizado · ${j.cronExpression}`;
      }
    }
    if (j.frequencyType === 'ONE_SHOT' && j.executeAt) {
      return `Una vez: ${j.executeAt}`;
    }
    return j.frequencyType;
  }

  setFrequencyType(value: string): void {
    this.formFrequencyType = value as UIFrequencyType;
  }

  getPreviewText(): string {
    const tz = this.formTimezone;
    switch (this.formFrequencyType) {
      case 'HOURLY':
        return `Cada ${this.formIntervalValue} hora(s)`;
      case 'DAILY':
        return `Cada día a las ${this.formStartTime} (${tz})`;
      case 'WEEKLY': {
        const dayLabels = this.formDaysOfWeek
          .map(
            (d) =>
              this.daysOfWeekOptions.find((o) => o.value === d)?.label ?? d,
          )
          .join(', ');
        return `Los ${dayLabels || '—'} a las ${this.formStartTime} (${tz})`;
      }
      case 'MONTHLY':
        return `El día ${this.formDayOfMonth} de cada mes a las ${this.formStartTime} (${tz})`;
      case 'CUSTOM':
        return `Expresión: ${this.formCronExpression}`;
      default:
        return '';
    }
  }
}
